import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["allowCreate", "anchorType", "autoComplete", "autoFocus", "autoload", "ButtonContent", "buttonContentProps", "buttonSize", "buttonUse", "clearable", "closeOnTargetLeave", "cssStyles", "disabled", "error", "id", "ignoreAccents", "ignoreCase", "inputRef", "inputValue", "isDisplayOptimized", "isLoading", "itemComponent", "loadOptions", "menuRenderer", "menuWidth", "multi", "multiCollapseLimit", "noResultsText", "onChange", "onInputValueChange", "onOpenChange", "onSelectedOptionChange", "open", "optionComponent", "optionRenderer", "options", "placeholder", "promptTextCreator", "readOnly", "resetValue", "searchable", "searchPlaceholder", "searchPromptText", "splitRegex", "tabIndex", "value", "valueComponent", "valueRenderer"];
import { Controllable } from '../Controllable';
import unescapedText from 'I18n/utils/unescapedText';
import { createRef, Component } from 'react';
import { findDOMNode } from 'react-dom';
// @ts-expect-error untyped dependency
import ReactSelectPlusSelect from 'react-select-plus';
import devLogger from 'react-utils/devLogger';
import { sequence } from '../../utils/functions';
import { MultiSelectValue } from './MultiSelectValue';
import { SelectWithButtonAnchor } from './SelectWithButtonAnchor';
import { SelectWithInputAnchor } from './SelectWithInputAnchor';
import { lazyEval } from '../../utils/lazyEval';
import memoizeOne from 'react-utils/memoizeOne';
import { Consumer as FieldsetContextConsumer } from '../../context/FieldsetContext';
import { isOptionGroup as _isOptionGroup } from './Select.types';
import { StyledReactSelectPlusWrapper } from './Select.styles';
import { getInputProps, getOptionsCache, getResetOption, filterInputValue, computeControlWidth, getPlaceholder, promptTextCreator as defaultPromptTextCreator } from './Select.helpers';
import { stripDiacritics } from './Select.constants';
import { createHandlers } from './Select.handlers';
import { createGetters } from './Select.getters';
import { createRenderers } from './Select.renderers';
import { createRefCallbacks } from './Select.refs';
import { Popover } from '../Popover';
import { createProxies } from './Select.proxies';
import { withTokenProps } from '../../../utils/withTokenProps';
import { filterProps } from '../../../utils/filterProps';
import { withDisplayOptimized } from '../../context/DisplayOptimizedContext';
import { FormControlFocusHoverContextConsumer } from '../../context/FormControlFocusHoverContext';
import { pick } from '../../utils/pick';
import { ControlProvider } from '../Link/ControlContext';
import { logComponentOrElementMemoizationWarning } from '../../utils/isComponentOrElementMemoized';
import { jsx as _jsx } from "react/jsx-runtime";
const defaultSplitRegex = /;/;
const isValidSingleValue = v => v != null;
export class SelectCore extends Component {
  static getDerivedStateFromProps(props, state) {
    return createGetters(null).getDerivedStateFromProps(props, state);
  }
  constructor(_props) {
    super(_props);
    this.state = {
      controlWidth: 0,
      initiallyOpen: false,
      inputValue: '',
      isLoading: false,
      optionsCache: {},
      paginationCache: {},
      prevOpen: false,
      resetOption: null
    };
    this.handlers = createHandlers(this);
    this.getters = createGetters(this);
    this.renderers = createRenderers(this);
    this.proxies = createProxies(this);
    this.refCallbacks = createRefCallbacks(this);
    this.popoverRef = /*#__PURE__*/createRef();
    this.optionRendererProxy = option => {
      return this.proxies.optionRendererProxy(option);
    };
    this.defaultValueRenderer = option => {
      return this.renderers.defaultValueRenderer(option);
    };
    this.filterOptionsProxy = (options, input, excludeOptions, ...rest) => {
      return this.proxies.filterOptionsProxy(options, input, excludeOptions, ...rest);
    };
    this.creatableOnInputChangeProxy = str => {
      return this.proxies.creatableOnInputChangeProxy(str);
    };
    this.handleChange = option => {
      return this.handlers.handleChange(option);
    };
    this.handleInputKeyDown = evt => {
      return this.handlers.handleInputKeyDown(evt);
    };
    this.handleOptionToggle = (option, evt) => {
      return this.handlers.handleOptionToggle(option, evt);
    };
    this.handleClose = evt => {
      return this.handlers.handleClose(evt);
    };
    this.handleOpen = evt => {
      return this.handlers.handleOpen(evt);
    };
    this.triggerOpen = () => {
      return this.handlers.triggerOpen();
    };
    this.handleInputChange = str => {
      return this.handlers.handleInputChange(str);
    };
    this.handleInputValueChange = evt => {
      return this.handlers.handleInputValueChange(evt);
    };
    this.handlePaste = evt => {
      return this.handlers.handlePaste(evt);
    };
    this.handleAutosize = () => {
      return this.handlers.handleAutosize();
    };
    this.handleMouseDownOnDropdown = evt => {
      return this.handlers.handleMouseDownOnDropdown(evt);
    };
    this.getSplitValuesFromString = (str, includeLastValue) => {
      return this.getters.getSplitValuesFromString(str, includeLastValue);
    };
    this.getMenuWidth = () => {
      return this.getters.getMenuWidth();
    };
    this.focus = () => {
      try {
        this._anchorRef.focus();
      } catch (e) {
        /* ¯\_(ツ)_/¯ */
      }
    };
    this.dropdownContentRefCallback = this.refCallbacks.dropdownContentRefCallback;
    this.virtualizedMenuRendererRefCallback = this.refCallbacks.virtualizedMenuRendererRefCallback;
    this.anchorRefCallback = this.refCallbacks.anchorRefCallback;
    this.selectRefCallback = this.refCallbacks.selectRefCallback;
    this.getInternalSelect = () => {
      return this.getters.getInternalSelect();
    };
    this.getInternalCreatableSelect = () => {
      return this.getters.getInternalCreatableSelect();
    };
    this.getInternalAsyncSelect = () => {
      return this.getters.getInternalAsyncSelect();
    };
    this.loadOptionsProxy = (input, loadOptionsCallback) => {
      return this.proxies.loadOptionsProxy(input, loadOptionsCallback);
    };
    this.checkForFormattedMessages = () => {
      const options = document.querySelectorAll('.Select-option>[data-typeahead-item]');
      const optionLinks = Array.from(options).filter(item => item.querySelectorAll('a[href][data-link]>i18n-string').length);
      if (optionLinks.length) return true;
      const optionsWithPopovers = document.querySelectorAll('.Select-option>[data-typeahead-item]>[data-popover-id]');
      const popoverIds = Array.from(optionsWithPopovers).map(span => span.getAttribute('aria-describedby'));
      const popoverLinks = popoverIds.filter(popoverId => {
        return document.querySelectorAll(`#${popoverId} a[href][data-link]>i18n-string`).length;
      });
      return !!popoverLinks.length;
    };
    this.renderDropdown = props => {
      const {
        closeOnTargetLeave,
        onOpenChange,
        open,
        placement
      } = this.props;
      return /*#__PURE__*/_jsx(Popover, {
        arrowSize: "none",
        autoPlacement: true // "dropdown"
        ,
        closeOnTargetLeave: closeOnTargetLeave,
        content: this.renderDropdownContent(props),
        onOpenChange: onOpenChange,
        open: open,
        placement: placement || 'bottom right',
        popoverRef: this.popoverRef,
        target: this.state.target == null ? undefined : this.state.target
      });
    };
    this.renderDropdownContent = props => {
      return this.renderers.renderDropdownContent(props);
    };
    const cacheOptions = getOptionsCache(_props, this.state);
    const resetOption = getResetOption(_props, this.state);
    this.state = Object.assign({}, this.state, cacheOptions, resetOption, {
      initiallyOpen: _props.open,
      prevOpen: _props.open
    });
    this._isMounted = true; // not strictly true, but componentDidMount() fires too late!
    this._getInputProps = memoizeOne(getInputProps);
  }
  componentDidUpdate(prevProps) {
    var _this$props$open;
    /* eslint-disable-next-line react/no-find-dom-node */
    const controlEl = findDOMNode(this._anchorRef);
    if (!controlEl) {
      return;
    }
    const controlWidth = computeControlWidth(controlEl, (_this$props$open = this.props.open) !== null && _this$props$open !== void 0 ? _this$props$open : false);
    if (controlWidth !== this.state.controlWidth) {
      this.setState({
        controlWidth
      });
    }
    if (!prevProps.open && this.props.open) {
      this.focus();
    }
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  areOptionsToggleable({
    anchorType,
    multi
  }) {
    // Return true if we show options as checkboxes in the dropdown (#1976)
    const computedAnchorType = this.getAnchorType({
      anchorType,
      multi
    });
    return Boolean(multi && computedAnchorType !== 'combined');
  }
  defaultOptionRenderer(option) {
    return this.renderers.defaultOptionRenderer(option);
  }
  // Extracted to a function so it can be stubbed out in tests
  getDefaultFilterOptionsFunc() {
    return ReactSelectPlusSelect.defaultProps.filterOptions;
  }
  selectValueProxy(selectValue, selectedOption) {
    return this.proxies.selectValueProxy(selectValue, selectedOption);
  }
  triggerLoadMore() {
    // Trick Select.Async into calling loadOptions again, then move focus up one option
    const internalSelect = this.getInternalSelect();
    const internalAsyncSelect = this.getInternalAsyncSelect();
    if (!internalSelect || !internalAsyncSelect || !internalAsyncSelect.loadOptions) {
      return;
    }
    const {
      ignoreAccents,
      ignoreCase
    } = this.props;
    const visibleOptions = internalSelect._visibleOptions;
    const loadMoreOption = visibleOptions[visibleOptions.length - 1];
    const transformedInputValue = filterInputValue(loadMoreOption.input, {
      ignoreAccents,
      ignoreCase
    });
    if (internalAsyncSelect._cache) {
      delete internalAsyncSelect._cache[transformedInputValue];
    }
    internalAsyncSelect.loadOptions(transformedInputValue);
    const normalOptions = internalSelect.props.options;
    internalSelect.setState({
      focusedOption: normalOptions[normalOptions.length - 1]
    });
  }
  toggleGroup(groupToToggle, willBeSelected) {
    const {
      value
    } = this.props;
    const valueCopy = Array.isArray(value) ? value.filter(isValidSingleValue) : [];
    let newValue = valueCopy;
    if (willBeSelected) {
      const addOptionValue = o => {
        if (_isOptionGroup(o)) {
          o.options.filter(option => !('disabled' in option && option.disabled)).forEach(addOptionValue);
        } else {
          if (newValue.indexOf(o.value) === -1) newValue.push(o.value);
        }
      };
      addOptionValue(groupToToggle);
    } else {
      const removeOptionValue = o => {
        if (_isOptionGroup(o)) {
          o.options.filter(option => !('disabled' in option && option.disabled)).forEach(removeOptionValue);
        } else {
          newValue = newValue.filter(v => v !== o.value);
        }
      };
      removeOptionValue(groupToToggle);
    }
    this.setMultiValues(newValue);
  }
  toggleMultiValue(valueToToggle, willBeSelected) {
    const {
      value
    } = this.props;
    const valueCopy = Array.isArray(value) ? value.filter(isValidSingleValue) : [];
    let newValue = null;
    if (willBeSelected) {
      newValue = valueCopy.concat(valueToToggle);
    } else {
      newValue = valueCopy.filter(v => v !== valueToToggle);
    }
    this.setMultiValues(newValue);
  }
  addMultiValues(addValues) {
    const {
      value
    } = this.props;
    const newValues = Array.isArray(value) ? value.filter(isValidSingleValue) : [];
    addValues.forEach(v => {
      if (newValues.indexOf(v) === -1 && v !== '') newValues.push(v);
    });
    this.setMultiValues(newValues);
  }
  setMultiValues(newValues) {
    const {
      optionsCache
    } = this.state;
    // Map values to options
    const newOptions = newValues.map(v => optionsCache[`${v}`] || {
      text: v,
      value: v
    });
    this.handleChange(newOptions);
  }
  getLoadOptionsProxyCallback(input, loadOptionsCallback) {
    return this.getters.getLoadOptionsProxyCallback(input, loadOptionsCallback);
  }
  getValueAsObject(value) {
    return this.getters.getValueAsObject(value);
  }
  isCollapsed({
    multi,
    multiCollapseLimit,
    open,
    value
  }) {
    return multiCollapseLimit != null && value != null && (Array.isArray(value) ? value.length : 0) > multiCollapseLimit && multi && !open;
  }
  getAnchorType({
    anchorType,
    multi
  }) {
    return this.getters.getAnchorType({
      anchorType,
      multi
    });
  }
  getNoResultsText({
    noResultsText
  }) {
    return this.getters.getNoResultsText({
      noResultsText
    });
  }
  getMenuRenderer({
    itemComponent,
    menuRenderer,
    menuWidth,
    optionRenderer,
    optionComponent
  }) {
    return this.getters.getMenuRenderer({
      itemComponent,
      menuRenderer,
      menuWidth,
      optionRenderer,
      optionComponent
    });
  }
  renderDropdownFooter() {
    return this.renderers.renderDropdownFooter();
  }
  renderCollapsedAnchor(error, multiCollapseLimit, multi, value, valueRenderer, rest) {
    return this.renderers.renderCollapsedAnchor(error, multiCollapseLimit, multi, value, valueRenderer, rest);
  }
  render() {
    const _this$props = this.props,
      {
        allowCreate = false,
        anchorType = 'auto',
        autoComplete = 'off',
        autoFocus,
        autoload = true,
        ButtonContent,
        buttonContentProps,
        buttonSize = 'medium',
        buttonUse = 'form',
        clearable = false,
        closeOnTargetLeave,
        cssStyles,
        disabled,
        error,
        id,
        ignoreAccents = true,
        ignoreCase = true,
        inputRef,
        inputValue,
        isDisplayOptimized,
        isLoading,
        itemComponent = 'span',
        loadOptions,
        menuRenderer,
        menuWidth,
        multi = false,
        multiCollapseLimit,
        noResultsText,
        onOpenChange,
        open = false,
        optionComponent,
        optionRenderer,
        options,
        placeholder,
        promptTextCreator = defaultPromptTextCreator,
        readOnly,
        resetValue = '',
        searchable = true,
        searchPlaceholder = unescapedText('salesUI.UISearchableSelectInput.placeholder'),
        searchPromptText = unescapedText('salesUI.UISelect.typeToSearch'),
        splitRegex = defaultSplitRegex,
        tabIndex,
        value = '',
        valueRenderer
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      initiallyOpen,
      resetOption
    } = this.state;
    const {
      domProps
    } = filterProps(rest);

    // Use an HOC around React Select, if applicable
    let SelectComponent = ReactSelectPlusSelect;
    if (loadOptions) {
      SelectComponent = ReactSelectPlusSelect.Async;
    } else if (allowCreate) {
      SelectComponent = ReactSelectPlusSelect.Creatable;
    }
    // Use the virtualizedMenuRenderer, if it's provided and compatible with the other props
    const computedMenuRenderer = this.getMenuRenderer({
      itemComponent,
      menuRenderer,
      menuWidth,
      optionRenderer,
      optionComponent
    });

    // Determine what type of control the dropdown is attached to
    const computedAnchorType = this.getAnchorType({
      anchorType,
      multi
    });

    // A null or undefined value should be considered equivalent to resetValue
    const computedValue = value == null ? resetValue : value;
    if (loadOptions && allowCreate) {
      devLogger.warn({
        message: 'Select: loadOptions and allowCreate are mutually exclusive.',
        key: 'Select: asyncCreatable'
      });
    }
    if (rest.autofocus) {
      devLogger.warn({
        message: "Select: The autofocus prop is deprecated in favor of autoFocus to match React's autoFocus prop",
        key: 'Select: autofocus'
      });
    }
    if (inputRef && computedAnchorType === 'combined') {
      devLogger.warn({
        message: 'Select: The inputRef prop is only supported with anchorType="input" or anchorType="button"',
        key: 'Select: inputRef'
      });
    }

    // Check if optionComponent is memoized (only warn once per component)
    if (optionComponent) {
      logComponentOrElementMemoizationWarning({
        componentOrElement: optionComponent,
        errorMessage: 'Select: optionComponent should be memoized with React.memo() to prevent unnecessary re-renders.',
        errorKey: 'Select: optionComponent not memoized'
      });
    }
    if (itemComponent) {
      logComponentOrElementMemoizationWarning({
        componentOrElement: itemComponent,
        errorMessage: 'Select: itemComponent should be memoized with React.memo() to prevent unnecessary re-renders.',
        errorKey: 'Select: itemComponent not memoized'
      });
    }

    // We need to attach two ref callbacks to the combined anchor.
    this._refSequence = this._refSequence || sequence(this.anchorRefCallback, this.selectRefCallback);

    // Bootstrap a read-only state on the "external" Select component by disabling interactivity
    // Only needed for the "combined" anchorType - readOnly class prevents disabled styles from being applied
    const readOnlyProps = readOnly ? {
      disabled: true
    } : {};
    const computedPlaceholder = getPlaceholder({
      isDisplayOptimized,
      placeholder
    });
    return /*#__PURE__*/_jsx(ControlProvider, {
      isControl: true,
      children: /*#__PURE__*/_jsx(FormControlFocusHoverContextConsumer, {
        children: ({
          focused,
          hovered
        }) => /*#__PURE__*/_jsx(FieldsetContextConsumer, {
          children: ({
            disabled: fieldDisabled,
            size: fieldSize
          }) => {
            const computedDisabled = disabled || fieldDisabled;
            const isDisabledOrReadOnly = computedDisabled || readOnly;

            // Props that are used regardless of anchor type
            const commonProps = Object.assign({}, rest, {
              allowCreate,
              autoload,
              backspaceRemoves: multi && computedAnchorType === 'combined',
              buttonSize: 'medium',
              clearable: computedAnchorType === 'combined' ? clearable : false,
              disabled: computedDisabled,
              filterOptions: this.filterOptionsProxy,
              id,
              ignoreAccents,
              ignoreCase,
              inputProps: this._getInputProps(autoComplete, computedDisabled, id, searchable, computedAnchorType, this.handleAutosize, this.handlePaste, readOnly),
              isDisplayOptimized,
              isLoading: isLoading != null ? isLoading : this.state.isLoading,
              isOpen: open && !isDisabledOrReadOnly && !!this.state.target,
              labelKey: 'text',
              loadOptions: loadOptions ? this.loadOptionsProxy : undefined,
              loadingPlaceholder: null,
              menuRenderer: computedMenuRenderer,
              multi,
              noResultsText: this.getNoResultsText({
                noResultsText
              }),
              onChange: this.handleChange,
              onClose: this.handleClose,
              onInputChange: this.handleInputChange,
              onInputKeyDown: this.handleInputKeyDown,
              onOpen: this.handleOpen,
              onPaste: this.handlePaste,
              openAfterFocus: true,
              optionRenderer: this.optionRendererProxy,
              options: loadOptions ? undefined : options,
              placeholder: computedPlaceholder,
              promptTextCreator,
              readOnly,
              ref: this.anchorRefCallback,
              renderInvalidValues: true,
              required: false,
              resetValue,
              searchable,
              searchPromptText: lazyEval(searchPromptText),
              splitRegex,
              tabIndex,
              tabSelectsValue: false,
              value: loadOptions ? this.getValueAsObject(computedValue) : computedValue,
              valueComponent: multi ? MultiSelectValue : ReactSelectPlusSelect.defaultProps.valueComponent,
              valueRenderer: valueRenderer || this.defaultValueRenderer
            });
            if (computedAnchorType === 'input') {
              // no `StyledReactSelectPlusWrapper` is necessary because `SelectWithInputAnchor` renders `SelectComponent` in a hidden div.
              // - the select control is not visible, so styles are not necessary
              // - input anchors don't show multi value tags
              // - the results dropdown provides its own styles for options and option groups
              return /*#__PURE__*/_jsx(SelectWithInputAnchor, Object.assign({}, commonProps, domProps, {
                autoFocus: autoFocus,
                dropdownComponent: this.renderDropdown,
                error: error,
                inputRef: inputRef,
                inputValue: inputValue,
                onInputValueChange: this.handleInputValueChange,
                onOpenChange: onOpenChange,
                selectComponent: SelectComponent,
                selectRef: this.selectRefCallback,
                open: open
              }));
            }
            if (this.isCollapsed({
              multi,
              multiCollapseLimit,
              open,
              value
            })) {
              return this.renderCollapsedAnchor(!!error, multiCollapseLimit, multi, value, valueRenderer, rest);
            }
            if (computedAnchorType === 'button') {
              // in button-type Selects, the StyledReactSelectPlusWrapper takes the place
              // of the <button> for sizing purposes (see notes in Select.style.tsx
              // about `display: contents`). the child <button> simply fill the wrapper.
              // as such, size token props need to be applied to the wrapper, not the child <button>
              const sizeCssStyles = cssStyles !== undefined ? pick(cssStyles,
              // sizing props accepted by SizeStyleProps
              'inlineSize', 'minInlineSize', 'maxInlineSize', 'blockSize', 'minBlockSize', 'maxBlockSize') : undefined;
              return (
                /*#__PURE__*/
                // `StyledReactSelectPlusWrapper` is necessary even though `SelectWithButtonAnchor` renders `SelectComponent` as hidden.
                // - it applies styles to PrivateButton's `StyledButton`, which impacts the button anchor's appearance
                _jsx(StyledReactSelectPlusWrapper, {
                  $multi: multi,
                  $anchorType: computedAnchorType,
                  $buttonUse: buttonUse,
                  $isDisplayOptimized: isDisplayOptimized,
                  $fieldSize: fieldSize,
                  $disabled: computedDisabled,
                  $readOnly: readOnly,
                  $cssStyles: sizeCssStyles,
                  $error: error,
                  $placeholder: computedPlaceholder !== undefined,
                  children: /*#__PURE__*/_jsx(SelectWithButtonAnchor, Object.assign({}, commonProps, domProps, {
                    autoComplete: autoComplete,
                    autoFocus: autoFocus,
                    anchorType: computedAnchorType,
                    buttonSize: buttonSize,
                    ButtonContent: ButtonContent,
                    buttonContentProps: buttonContentProps,
                    buttonUse: buttonUse,
                    cssStyles: cssStyles,
                    closeOnTargetLeave: closeOnTargetLeave,
                    dropdownContentRef: this.dropdownContentRefCallback,
                    dropdownFooter: this.renderDropdownFooter(),
                    error: error,
                    initiallyOpen: initiallyOpen,
                    inputRef: inputRef,
                    menuWidth: menuWidth,
                    onInputValueChange: this.handleInputValueChange,
                    onOpenChange: onOpenChange,
                    resetOption: resetOption === null ? undefined : resetOption,
                    searchPlaceholder: searchPlaceholder,
                    selectComponent: SelectComponent,
                    selectRef: this.selectRefCallback,
                    focused: focused,
                    hovered: hovered
                  }))
                })
              );
            }
            return /*#__PURE__*/_jsx(StyledReactSelectPlusWrapper, Object.assign({
              $multi: multi,
              $anchorType: computedAnchorType,
              $buttonUse: buttonUse,
              $isDisplayOptimized: isDisplayOptimized,
              $fieldSize: fieldSize,
              $disabled: computedDisabled,
              $readOnly: readOnly,
              $cssStyles: cssStyles,
              $error: error,
              $placeholder: computedPlaceholder !== undefined,
              "data-fnd-select-wrapper": "t" // required for testing Select anchorType = combined. See https://git.hubteam.com/HubSpot/UIComponents/pull/13080
            }, domProps, {
              children: /*#__PURE__*/_jsx(SelectComponent, Object.assign({}, commonProps, {
                "aria-invalid": !!error,
                autofocus: autoFocus,
                className: "fnd-select" // Needed for testing
                ,
                dropdownComponent: this.renderDropdown,
                onOpenChange: onOpenChange,
                ref: this._refSequence,
                tabIndex: typeof tabIndex === 'number' ? String(tabIndex) : tabIndex
              }, readOnlyProps))
            }));
          }
        })
      })
    });
  }
}
export const SelectCoreFunc = props => {
  return /*#__PURE__*/_jsx(SelectCore, Object.assign({}, props));
};
const ControllableSelect = Controllable(SelectCore, ['open', 'value']);
export const Select = withTokenProps(withDisplayOptimized(ControllableSelect));
Select.stripDiacritics = stripDiacritics;
Select.displayName = 'Select';